"use client";

import * as React from "react";
import { AlertTriangle } from "lucide-react";
import { StatusBadge } from "../../../feedback/status-badge";
import { Tooltip, TooltipContent, TooltipTrigger } from "../../../primitives/tooltip";
import { cn } from "../../../utils";
import { useLazyOverlay } from "./use-lazy-overlay";

export interface CellErrorProps {
  /** Last known value before failure, rendered muted. */
  previousValue?: React.ReactNode;
  errorMessage?: string;
  attempt?: number;
  className?: string;
}

/**
 * Failed cell run: muted previous-value placeholder + error badge with
 * tooltip carrying the error message.
 */
export function CellError({
  previousValue,
  errorMessage = "Cell run failed.",
  attempt,
  className,
}: CellErrorProps) {
  const { primed, triggerProps } = useLazyOverlay();
  return (
    <span className={cn("inline-flex min-w-0 items-center gap-1.5 text-sm", className)}>
      <span className="min-w-0 truncate text-muted-foreground italic">{previousValue ?? "—"}</span>
      <Tooltip>
        <TooltipTrigger asChild>
          <span aria-label="Error" {...triggerProps}>
            <StatusBadge variant="failed" icon={AlertTriangle} className="shrink-0">
              <span className="sr-only">Error</span>
            </StatusBadge>
          </span>
        </TooltipTrigger>
        {primed && (
          <TooltipContent className="max-w-xs">
            <div className="space-y-1">
              <div className="font-medium">Cell run failed</div>
              <div className="text-xs">{errorMessage}</div>
              {attempt != null && (
                <div className="text-[10px] text-muted-foreground">Attempt #{attempt}</div>
              )}
            </div>
          </TooltipContent>
        )}
      </Tooltip>
    </span>
  );
}
